import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalBody,
  Box,
  Text,
  Flex,
  Button,
  IconButton,
  Divider,
  Grid,
  Badge,
  useToast,
  Icon,
  Link,
  ChakraProvider,
  Spinner,
  VStack,
  Card,
  CardHeader,
  CardBody,
  GridItem,
} from "@chakra-ui/react";
import { CiSearch } from "react-icons/ci";
import {
  FaShieldAlt,
  FaFileAlt,
  FaRegCopy,
  FaFileDownload,
} from "react-icons/fa";
import { PiEngineFill } from "react-icons/pi";
import { GoHistory } from "react-icons/go";
import { MdLocationOn, MdMessage } from "react-icons/md";
import { useEffect, useState } from "react";
import { cpfFormatter } from "@/utils/cpfFormatter";
import { BrlCurrencyFormatter } from "@/utils/BrlCurrencyFormatter";
import { format } from "date-fns";
import {
  getFormalizationStatusLabel,
  getProposalStatusDescription,
  ProposalStatusType,
} from "@/utils/getProposalStatusLabel";
import { useNavigate, useParams } from "react-router-dom";
import createTheme from "../../../../theme/theme.ts";
import FilePreview from "@/components/FilePreview/index.tsx";
import BankDetailModal from "@/components/BankDetailModal/index.tsx";
import BankAndPersonalDetailModal from "@/components/BankAndPersonalDetailModal.ts/index.tsx";
import { getConsignadoProposalById, rejectProposalConsignado } from "@/services/consignado/proposal.service.ts";
import { ProposalConsignadoCustomStatus, IConsignadoProposalDetail } from "@/models/consignado/proposal.model.ts";
import { ProposalLogs } from "../ProposalLogs/ProposalLogs.tsx";
import { ProposalStatus, StatusRenderer } from "@/utils/consignado/proposalStatusRenderer.tsx";
import { CreditEngineReportProposal } from "../CreditEngineReportProposal/index.tsx";
import { ReportProposalSendEmail } from "../ReportProposalSendEmail/index.tsx";
import { phoneFormatter } from "@/utils/formatPhone.ts";
import { getOfertaEscolhida } from "@/utils/consignado/ofertaEscolhida";
// import BankAndPersonalDetailModal from "../BankAndPersonalDetailModal.ts";
// import BankDetailModal from "../BankDetailModal";

interface ProposalModalProps {
  isOpen: boolean;
  onClose: () => void;
  proposalId: string;
}

type ActiveTabType = "visualizar" | "seguranca" | "contrato" | "historico" | "analise-credito" | "ações" | "disparo-mensagem" | "";

export default function ProposalModalConsignado({
  isOpen,
  onClose,
  proposalId,
}: ProposalModalProps) {

  const [activeTab, setActiveTab] = useState<ActiveTabType>("");
  const { id: paramId } = useParams<{ id: string }>();

  const finalId = proposalId ?? paramId ?? null;
  const toast = useToast();
  const [proposal, setProposal] = useState<IConsignadoProposalDetail | null>(null);
  const [isLoading, setIsLoading] = useState(false);;
  const { oferta, seguro, valorSeguro } = getOfertaEscolhida(proposal?.custom_status);

  const [isBankDetailFormOpen, setIsBankDetailFormOpen] = useState(false);
  const [isBankAndPersonalDetailFormOpen, setIsBankAndPersonalDetailFormOpen] = useState(false);
  const navigate = useNavigate();

  const computedIsOpen = isOpen ?? Boolean(paramId);
  const handleCloseModal = onClose ?? (() => {
    navigate("/propostas");
  });

  useEffect(() => {
    if (!finalId) return;
    let alive = true;
    if (computedIsOpen) {
      setIsLoading(true);
      getConsignadoProposalById(finalId)
        .then((data) => {
          if (alive) setProposal(data ?? null);
        })
        .catch((err) => console.error(err))
        .finally(() => {
          if (alive) setIsLoading(false);
        });
      return () => {
        alive = false;
      };
    }
  }, [computedIsOpen]);

  const handleCopy = (text: string) => {
    navigator.clipboard.writeText(text);
    toast({
      title: "Copiado",
      description: "Texto copiado para a área de transferência.",
      status: "success",
      duration: 2000,
      isClosable: true,
    });
  };

  const handleCancelProposal = async (
    event: React.MouseEvent<HTMLButtonElement>,
    id: string
  ) => {
    event.stopPropagation();
    try {
      await rejectProposalConsignado(id);
      toast({
        title: "Proposta Cancelada",
        description: "Enviamos a proposta para cancelamento.",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: "Erro ao Cancelar",
        description: "Ocorreu um erro ao cancelar a proposta.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const getModalSize = () => (activeTab === "seguranca" ? "2xl" : "xl");

  const theme = createTheme();

  const handleViewMap = (locale: any) => {
    if (!locale) return;
    window.open(`https://www.google.com/maps/search/?api=1&query=${locale.lat}%2C${locale.log}`, "_blank");
  }
  return (
    <>
      <Modal isOpen={computedIsOpen} onClose={handleCloseModal} size={getModalSize()}>
        <ModalOverlay />
        <ModalContent
          borderRadius={{ base: "20px", md: "20px" }}
          overflow="hidden"
          maxW={{ base: "90%", md: "90%", lg: "800px" }}
        >
          {isLoading ? (
            <ChakraProvider theme={theme}>
              <Box
                height="100%"
                display="flex"
                alignItems="center"
                justifyContent="center"
                padding="3em"
              >
                <VStack spacing={4}>
                  <Spinner size="xl" color="primary" thickness="4px" speed="0.65s" />
                  <Text fontSize="xl" fontWeight="medium">
                    Carregando...
                  </Text>
                </VStack>
              </Box>
            </ChakraProvider>
          ) : !proposal ? (
            <Box p={6}><Text>Proposta não encontrada.</Text></Box>
          ) : (<>
            {activeTab !== "" && (
              <Box
                bg="#58c46c"
                pt={2}
                pl={{ base: 3, md: 5 }}
                pb={2}
                borderTopRadius={{ base: "0", md: "20px" }}
              >
                <Text
                  color="white"
                  fontSize={{ base: "md", md: "sm" }}
                  fontFamily="Lexend Deca"
                >
                  Proposta
                </Text>
                <Text
                  color="white"
                  fontSize={{ base: "md", md: "lg" }}
                  fontFamily="Lexend Deca"
                >
                  {proposal?.cliente_nome}
                </Text>
              </Box>
            )}

            <Box
              bg="secondary"
              p={{ base: 3, md: 4 }}
              borderTopRadius={{ base: "0", md: activeTab === "" ? "20px" : "0" }}
            >
              <Grid
                templateColumns={{ base: "repeat(2, 1fr)", md: "repeat(3, 1fr)" }}
                gap={3}
              >
                <Flex
                  align="center"
                  justify="center"
                  color="white"
                  bg="rgba(255, 255, 255, 0.15)"
                  p={2}
                  borderRadius="10px"
                  h={{ base: "40px", md: "35px" }}
                  boxShadow="sm"
                  cursor="pointer"
                  onClick={() => setActiveTab("visualizar")}
                  _hover={{ bg: "rgba(255, 255, 255, 0.25)" }}
                  transition="all 0.2s"
                >
                  <Icon as={CiSearch} boxSize={{ base: 4, md: 5 }} mr={1} />
                  <Text
                    fontSize={{ base: "sm", md: "sm" }}
                    fontFamily="Lexend Deca"
                  >
                    Visualizar
                  </Text>
                </Flex>

                <Flex
                  align="center"
                  justify="center"
                  color="white"
                  bg="rgba(255, 255, 255, 0.15)"
                  p={2}
                  borderRadius="10px"
                  h={{ base: "40px", md: "35px" }}
                  boxShadow="sm"
                  cursor="pointer"
                  onClick={() => setActiveTab("seguranca")}
                  _hover={{ bg: "rgba(255, 255, 255, 0.25)" }}
                  transition="all 0.2s"
                >
                  <Icon as={FaShieldAlt} boxSize={{ base: 4, md: 5 }} mr={1} />
                  <Text
                    fontSize={{ base: "sm", md: "sm" }}
                    fontFamily="Lexend Deca"
                  >
                    Formalização
                  </Text>
                </Flex>

                <Flex
                  align="center"
                  justify="center"
                  color="white"
                  bg="rgba(255, 255, 255, 0.15)"
                  p={2}
                  borderRadius="10px"
                  h={{ base: "40px", md: "35px" }}
                  boxShadow="sm"
                  cursor="pointer"
                  onClick={() => setActiveTab("contrato")}
                  _hover={{ bg: "rgba(255, 255, 255, 0.25)" }}
                  transition="all 0.2s"
                >
                  <Icon as={FaFileAlt} boxSize={{ base: 4, md: 5 }} mr={1} />
                  <Text
                    fontSize={{ base: "sm", md: "sm" }}
                    fontFamily="Lexend Deca"
                  >
                    Contrato
                  </Text>
                </Flex>

                <Flex
                  align="center"
                  justify="center"
                  color="white"
                  bg="rgba(255, 255, 255, 0.15)"
                  p={2}
                  borderRadius="10px"
                  h={{ base: "40px", md: "35px" }}
                  boxShadow="sm"
                  cursor="pointer"
                  onClick={() => setActiveTab("historico")}
                  _hover={{ bg: "rgba(255, 255, 255, 0.25)" }}
                  transition="all 0.2s"
                >
                  <Icon as={GoHistory} boxSize={{ base: 4, md: 5 }} mr={1} />
                  <Text
                    fontSize={{ base: "sm", md: "sm" }}
                    fontFamily="Lexend Deca"
                  >
                    Histórico
                  </Text>
                </Flex>

                <Flex
                  align="center"
                  justify="center"
                  color="white"
                  bg="rgba(255, 255, 255, 0.15)"
                  p={2}
                  borderRadius="10px"
                  h={{ base: "40px", md: "35px" }}
                  boxShadow="sm"
                  cursor="pointer"
                  onClick={() => setActiveTab("analise-credito")}
                  _hover={{ bg: "rgba(255, 255, 255, 0.25)" }}
                  transition="all 0.2s"
                >
                  <Icon as={PiEngineFill} boxSize={{ base: 4, md: 5 }} mr={1} />
                  <Text
                    fontSize={{ base: "sm", md: "sm" }}
                    fontFamily="Lexend Deca"
                  >
                    Análise crédito
                  </Text>
                </Flex>

                <Flex
                  align="center"
                  justify="center"
                  color="white"
                  bg="rgba(255, 255, 255, 0.15)"
                  p={2}
                  borderRadius="10px"
                  h={{ base: "40px", md: "35px" }}
                  boxShadow="sm"
                  cursor="pointer"
                  onClick={() => setActiveTab("disparo-mensagem")}
                  _hover={{ bg: "rgba(255, 255, 255, 0.25)" }}
                  transition="all 0.2s"
                >
                  <Icon as={MdMessage} boxSize={{ base: 4, md: 5 }} mr={1} />
                  <Text
                    fontSize={{ base: "sm", md: "sm" }}
                    fontFamily="Lexend Deca"
                    whiteSpace="nowrap"
                  >
                    Disparo de mensagem
                  </Text>
                </Flex>
                {/* 
            EXLUIR ESSE BOTÃO
            <Flex
              align="center"
              color="white"
              bgGradient="linear(to-b, rgba(0, 0, 0, 0.5), secondary)"
              p={3}
              borderRadius="10px"
              mb={{ base: 2, md: 0 }}
              w={{ base: "130px", md: "130px" }}
              h={{ base: "40px", md: "30px" }}
              boxShadow="sm"
              cursor="pointer"
              onClick={() => setActiveTab("ações")}
            >
              <Icon as={GoZap} boxSize={{ base: 5, md: 6 }} mr={2} />
              <Text
                fontSize={{ base: "md", md: "md" }}
                fontFamily="Lexend Deca"
              >
                Ações
              </Text>
            </Flex> */}
              </Grid>
            </Box>

            <ModalBody p={{ base: 4, md: 6 }}>
              {activeTab === "" && (
                <Box
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  flexDirection="column"
                  width="100%"
                >
                  <Text
                    fontSize={{ base: "2xl", md: "4xl" }}
                    color="secondary"
                    fontFamily="Lexend Deca"
                    textAlign="center"
                    mb={2}
                  >
                    {proposal?.cliente_nome}
                  </Text>
                  <Flex align="center" justify="center" mb={2}>
                    <Text
                      fontSize={{ base: "2xl", md: "4xl" }}
                      color="secondary"
                      fontFamily="Lexend Deca"
                      textAlign="center"
                    >
                      CPF: {cpfFormatter(proposal?.cliente_cpf)}
                    </Text>
                    <IconButton
                      aria-label="Copiar CPF"
                      icon={<FaRegCopy />}
                      ml={2}
                      onClick={() =>
                        handleCopy(cpfFormatter(proposal?.cliente_cpf))
                      }
                      size={{ base: "xl", md: "sm" }}
                      variant="ghost"
                      color="gray.500"
                      alignSelf="center"
                    />
                  </Flex>

                  <Box width={{ base: "100%", md: "80%", lg: "400px" }} mt="1rem">
                    <Text
                      fontSize={{ base: "xl", md: "lg" }}
                      color="gray.400"
                      fontWeight="bold"
                      fontFamily="Lexend Deca"
                      display={
                        ["CANCELED", "CANCELED_BY_CLIENT", "REPROVED"].includes(
                          proposal?.status_proposta!
                        )
                          ? "flex"
                          : "none"
                      }
                    >
                      Motivo do cancelamento:
                    </Text>
                    <Flex
                      alignItems="center"
                      mb={4}
                      display={
                        ["CANCELED", "CANCELED_BY_CLIENT", "REPROVED"].includes(
                          proposal?.status_proposta!
                        )
                          ? "flex"
                          : "none"
                      }
                    >
                      <Text
                        fontSize={{ base: "md", md: "lg" }}
                        color="secondary"
                        fontFamily="Lexend Deca"
                      >
                        {getProposalStatusDescription(
                          proposal?.status_proposta! as ProposalStatusType,
                          proposal?.custom_status!
                        )}
                      </Text>
                      <IconButton
                        aria-label="Copiar Legenda da consulta"
                        ml={2}
                        icon={<FaRegCopy />}
                        onClick={() =>
                          handleCopy(
                            getProposalStatusDescription(
                              proposal?.status_proposta! as ProposalStatusType
                            )
                          )
                        }
                        size={{ base: "xl", md: "sm" }}
                        variant="ghost"
                        color="gray.500"
                      />
                    </Flex>
                    <Flex justify={"center"}>
                      {proposal?.status_proposta !== "PENDING_USER_REVIEW" && (
                        <StatusRenderer
                          status={
                            ProposalStatus[
                            proposal?.status_proposta as keyof typeof ProposalStatus
                            ]
                          }
                          customStatus={proposal?.custom_status as string}
                        />
                      )}
                    </Flex>
                    {proposal?.status_proposta === "PENDING_USER_REVIEW" && proposal?.custom_status && (
                      <Flex justify={"center"}>
                        <StatusRenderer status={ProposalStatus[proposal.status_proposta as keyof typeof ProposalStatus]} customStatus={proposal?.custom_status as string} />
                      </Flex>
                    )}
                    <Text
                      fontSize={{ base: "xl", md: "lg" }}
                      color="gray.400"
                      fontWeight="bold"
                      fontFamily="Lexend Deca"
                    >
                      Número da Proposta:
                    </Text>

                    <Flex alignItems="center" mb={4}>
                      <Text
                        fontSize={{ base: "xl", md: "lg" }}
                        color="secondary"
                        fontFamily="Lexend Deca"
                      >
                        {proposal?.short_hash}
                      </Text>
                      <IconButton
                        aria-label="Copiar Número da Proposta"
                        icon={<FaRegCopy />}
                        ml={2}
                        onClick={() => handleCopy(proposal?.short_hash!)}
                        size={{ base: "xl", md: "sm" }}
                        variant="ghost"
                        color="gray.500"
                      />
                    </Flex>
                    <Text
                      fontSize={{ base: "xl", md: "lg" }}
                      color="gray.400"
                      fontWeight="bold"
                      fontFamily="Lexend Deca"
                    >
                      Número da Proposta na Bancarizadora:
                    </Text>

                    <Flex alignItems="center" mb={4}>
                      <Text
                        fontSize={{ base: "xl", md: "lg" }}
                        color="secondary"
                        fontFamily="Lexend Deca"
                      >
                        {proposal?.id_externo || "Não informado"}
                      </Text>
                      <IconButton
                        aria-label="Copiar Número da Proposta"
                        icon={<FaRegCopy />}
                        ml={2}
                        onClick={() => handleCopy(proposal?.id_externo!)}
                        size={{ base: "xl", md: "sm" }}
                        variant="ghost"
                        color="gray.500"
                      />
                    </Flex>


                    <Text
                      fontSize={{ base: "xl", md: "lg" }}
                      color="gray.400"
                      fontWeight="bold"
                      fontFamily="Lexend Deca"
                    >
                      Número da CCB:
                    </Text>

                    <Flex alignItems="center" mb={4}>
                      <Text
                        fontSize={{ base: "xl", md: "lg" }}
                        color="secondary"
                        fontFamily="Lexend Deca"
                      >
                        {proposal?.numero_ccb || "Não informado"}
                      </Text>
                      <IconButton
                        aria-label="Copiar Número da Proposta"
                        icon={<FaRegCopy />}
                        ml={2}
                        onClick={() => handleCopy(proposal?.numero_ccb!)}
                        size={{ base: "xl", md: "sm" }}
                        variant="ghost"
                        color="gray.500"
                      />
                    </Flex>

                    <Text
                      fontSize={{ base: "xl", md: "lg" }}
                      color="gray.400"
                      fontWeight="bold"
                      fontFamily="Lexend Deca"
                    >
                      Link de Formalização:
                    </Text>
                    <Flex alignItems="center" mt={2} flexWrap="nowrap">
                      <Text
                        fontSize={{ base: "xl", md: "lg" }}
                        color="secondary"
                        fontFamily="Lexend Deca"
                      >
                        {proposal?.formalizacao_url ? proposal.formalizacao_url : "Erro ao obter link"}
                      </Text>
                      <IconButton
                        ml={2}
                        aria-label="Copiar Link de Formalização"
                        icon={<FaRegCopy />}
                        onClick={() =>
                          handleCopy(proposal?.formalizacao_url ? proposal.formalizacao_url : "Erro ao obter link")
                        }
                        size="sm"
                        variant="ghost"
                        color="gray.500"
                      />
                    </Flex>
                  </Box>
                </Box>
              )}


              {activeTab === "visualizar" && (
                <>
                  <VStack spacing={1} align="stretch">
                    <Flex
                      justify="space-between"
                      flexDirection={{ base: "column", md: "row" }}
                    >
                      <Text
                        fontSize={{ base: "md", md: "md" }}
                        fontFamily="Lexend Deca"
                        color="gray.500"
                      >
                        Titular:
                      </Text>
                      <Text
                        fontSize={{ base: "md", md: "md" }}
                        fontFamily="Lexend Deca"
                        color="gray.500"
                      >
                        {proposal?.cliente_nome ?? "Não informado"}
                      </Text>
                    </Flex>
                    <Divider borderColor="gray.500" borderWidth="1px" />
                    <Flex
                      justify="space-between"
                      flexDirection={{ base: "column", md: "row" }}
                    >
                      <Text
                        fontSize={{ base: "md", md: "md" }}
                        fontFamily="Lexend Deca"
                        color="gray.500"
                      >
                        CPF Titular:
                      </Text>
                      <Text
                        fontSize={{ base: "md", md: "md" }}
                        fontFamily="Lexend Deca"
                        color="gray.500"
                      >
                        {proposal?.cliente_cpf
                          ? cpfFormatter(proposal?.cliente_cpf)
                          : "Não informado"}
                      </Text>
                    </Flex>
                    <Divider borderColor="gray.500" borderWidth="1px" />
                    <Flex
                      justify="space-between"
                      flexDirection={{ base: "column", md: "row" }}
                    >
                      <Text
                        fontSize={{ base: "md", md: "md" }}
                        fontFamily="Lexend Deca"
                        color="gray.500"
                      >
                        telefone:
                      </Text>
                      <Text
                        fontSize={{ base: "md", md: "md" }}
                        fontFamily="Lexend Deca"
                        color="gray.500"
                      >
                        {proposal?.telefone
                          ? phoneFormatter(proposal?.telefone)
                          : "Não informado"}
                      </Text>
                    </Flex>
                    <Divider borderColor="gray.500" borderWidth="1px" />
                    <Flex
                      justify="space-between"
                      flexDirection={{ base: "column", md: "row" }}
                    >
                      <Text
                        fontSize={{ base: "md", md: "md" }}
                        fontFamily="Lexend Deca"
                        color="gray.500"
                      >
                        Tabela:{" "}
                      </Text>
                      <Text
                        fontSize={{ base: "md", md: "md" }}
                        fontFamily="Lexend Deca"
                        color="gray.500"
                      >
                        {oferta?.numberOfInstallments ? `${oferta.numberOfInstallments} Meses` : "-"}
                      </Text>
                    </Flex>
                    <Divider borderColor="gray.500" borderWidth="1px" />
                    <Flex
                      justify="space-between"
                      flexDirection={{ base: "column", md: "row" }}
                    >
                      <Text
                        fontSize={{ base: "sm", md: "md" }}
                        fontFamily="Lexend Deca"
                        color="gray.500"
                      >
                        Banco:
                      </Text>
                      <Text
                        fontSize={{ base: "sm", md: "md" }}
                        fontFamily="Lexend Deca"
                        color="gray.500"
                      >
                        {proposal?.numero_banco ?? "Não informado"}
                      </Text>
                    </Flex>
                    <Divider borderColor="gray.500" borderWidth="1px" />

                    <Flex
                      justify="space-between"
                      flexDirection={{ base: "column", md: "row" }}
                    >
                      <Text
                        fontSize={{ base: "sm", md: "md" }}
                        fontFamily="Lexend Deca"
                        color="gray.500"
                      >
                        Agência:
                      </Text>
                      <Text
                        fontSize={{ base: "sm", md: "md" }}
                        fontFamily="Lexend Deca"
                        color="gray.500"
                      >
                        {proposal?.agencia ?? "Não informado"}
                      </Text>
                    </Flex>
                    <Divider borderColor="gray.500" borderWidth="1px" />

                    <Flex
                      justify="space-between"
                      flexDirection={{ base: "column", md: "row" }}
                    >
                      <Text
                        fontSize={{ base: "sm", md: "md" }}
                        fontFamily="Lexend Deca"
                        color="gray.500"
                      >
                        Digito Agência:
                      </Text>
                      <Text
                        fontSize={{ base: "sm", md: "md" }}
                        fontFamily="Lexend Deca"
                        color="gray.500"
                      >
                        {proposal?.digito_agencia ?? "Não informado"}
                      </Text>
                    </Flex>
                    <Divider borderColor="gray.500" borderWidth="1px" />

                    <Flex
                      justify="space-between"
                      flexDirection={{ base: "column", md: "row" }}
                    >
                      <Text
                        fontSize={{ base: "sm", md: "md" }}
                        fontFamily="Lexend Deca"
                        color="gray.500"
                      >
                        Conta:
                      </Text>
                      <Text
                        fontSize={{ base: "sm", md: "md" }}
                        fontFamily="Lexend Deca"
                        color="gray.500"
                      >
                        {proposal?.conta ?? "Não informado"}
                      </Text>
                    </Flex>
                    <Divider borderColor="gray.500" borderWidth="1px" />

                    <Flex
                      justify="space-between"
                      flexDirection={{ base: "column", md: "row" }}
                    >
                      <Text
                        fontSize={{ base: "sm", md: "md" }}
                        fontFamily="Lexend Deca"
                        color="gray.500"
                      >
                        Dígito Verificador:
                      </Text>
                      <Text
                        fontSize={{ base: "sm", md: "md" }}
                        fontFamily="Lexend Deca"
                        color="gray.500"
                      >
                        {proposal?.digito_conta ?? "Não informado"}
                      </Text>
                    </Flex>
                    <Divider borderColor="gray.500" borderWidth="1px" />

                    <Flex
                      justify="space-between"
                      width="100%"
                      flexDirection={{ base: "column", md: "row" }}
                    >
                      <Text
                        fontSize={{ base: "md", md: "md" }}
                        fontFamily="Lexend Deca"
                        color="gray.500"
                      >
                        Data da Proposta:
                      </Text>
                      <Text
                        fontSize={{ base: "md", md: "md" }}
                        fontFamily="Lexend Deca"
                        color="gray.500"
                      >
                        {proposal?.data_proposta
                          ? format(
                            proposal?.data_proposta,
                            "dd/MM/yyyy - HH:mm"
                          )
                          : ""}
                      </Text>
                    </Flex>
                    <Divider borderColor="gray.500" borderWidth="1px" />
                    <Flex
                      justify="space-between"
                      flexDirection={{ base: "column", md: "row" }}
                    >
                      <Text
                        fontSize={{ base: "md", md: "md" }}
                        fontFamily="Lexend Deca"
                        color="gray.500"
                      >
                        Data de Formalização:
                      </Text>
                      <Text
                        fontSize={{ base: "md", md: "md" }}
                        fontFamily="Lexend Deca"
                        color="gray.500"
                      >
                        {proposal?.data_formalizacao
                          ? format(
                            proposal?.data_formalizacao,
                            "dd/MM/yyyy - HH:mm"
                          )
                          : ""}
                      </Text>
                    </Flex>
                    <Divider borderColor="gray.500" borderWidth="1px" />
                    <Flex
                      justify="space-between"
                      flexDirection={{ base: "column", md: "row" }}
                    >
                      <Text
                        fontSize={{ base: "sm", md: "md" }}
                        fontFamily="Lexend Deca"
                        color="gray.500"
                      >
                        Data do Pagamento:
                      </Text>
                      <Text
                        fontSize={{ base: "sm", md: "md" }}
                        fontFamily="Lexend Deca"
                        color="gray.500"
                      >
                        {proposal?.data_pagamento
                          ? format(proposal?.data_pagamento, "dd/MM/yyyy - HH:mm")
                          : ""}
                      </Text>
                    </Flex>
                    <Divider borderColor="gray.500" borderWidth="1px" />

                    <Flex
                      justify="space-between"
                      flexDirection={{ base: "column", md: "row" }}
                    >
                      <Text
                        fontSize={{ base: "sm", md: "md" }}
                        fontFamily="Lexend Deca"
                        color="gray.500"
                      >
                        Id Externo / Id Proposta da Seguradora
                      </Text>
                      <Text
                        fontSize={{ base: "sm", md: "md" }}
                        fontFamily="Lexend Deca"
                        color="gray.500"
                      >
                        {proposal?.id_externo_seguradora ?? "Valor não disponível"}
                      </Text>
                    </Flex>
                    <Divider borderColor="gray.500" borderWidth="1px" />
                    <Flex
                      justify="space-between"
                      flexDirection={{ base: "column", md: "row" }}
                    >
                      <Text
                        fontSize={{ base: "sm", md: "md" }}
                        fontFamily="Lexend Deca"
                        color="gray.500"
                      >
                        Id da Apólice / bilhete
                      </Text>
                      <Text
                        fontSize={{ base: "sm", md: "md" }}
                        fontFamily="Lexend Deca"
                        color="gray.500"
                      >
                        {seguro?.policyId ?? "Valor não disponível"}
                      </Text>
                    </Flex>
                    <Divider borderColor="gray.500" borderWidth="1px" />
                    <Flex
                      justify="space-between"
                      flexDirection={{ base: "column", md: "row" }}
                    >
                      <Text
                        fontSize={{ base: "sm", md: "md" }}
                        fontFamily="Lexend Deca"
                        color="gray.500"
                      >
                        IOF
                      </Text>
                      <Text
                        fontSize={{ base: "sm", md: "md" }}
                        fontFamily="Lexend Deca"
                        color="gray.500"
                      >
                        {oferta?.iofAmount ?? "Valor não disponível"}
                      </Text>
                    </Flex>
                    <Divider borderColor="gray.500" borderWidth="1px" />
                    <Flex
                      justify="space-between"
                      flexDirection={{ base: "column", md: "row" }}
                    >
                      <Text
                        fontSize={{ base: "sm", md: "md" }}
                        fontFamily="Lexend Deca"
                        color="gray.500"
                      >
                        TAXA A.A
                      </Text>
                      <Text
                        fontSize={{ base: "sm", md: "md" }}
                        fontFamily="Lexend Deca"
                        color="gray.500"
                      >
                        {oferta?.annualRate ? `${oferta.annualRate.toFixed(2)}%` : "Valor não disponível"}
                      </Text>
                    </Flex>
                    <Divider borderColor="gray.500" borderWidth="1px" />
                    <Flex
                      justify="space-between"
                      flexDirection={{ base: "column", md: "row" }}
                    >
                      <Text
                        fontSize={{ base: "sm", md: "md" }}
                        fontFamily="Lexend Deca"
                        color="gray.500"
                      >
                        CET - A.M
                      </Text>
                      <Text
                        fontSize={{ base: "sm", md: "md" }}
                        fontFamily="Lexend Deca"
                        color="gray.500"
                      >
                        {oferta?.monthlyCETAmount ? `${oferta.monthlyCETAmount.toFixed(2)}%` : "Valor não disponível"}
                      </Text>
                    </Flex>
                    <Divider borderColor="gray.500" borderWidth="1px" />
                    <Flex
                      justify="space-between"
                      flexDirection={{ base: "column", md: "row" }}
                    >
                      <Text
                        fontSize={{ base: "sm", md: "md" }}
                        fontFamily="Lexend Deca"
                        color="gray.500"
                      >
                       CET - A.A
                      </Text>
                      <Text
                        fontSize={{ base: "sm", md: "md" }}
                        fontFamily="Lexend Deca"
                        color="gray.500"
                      >
                        {oferta?.annualCETAmount ? `${oferta.annualCETAmount.toFixed(2)}%` : "Valor não disponível"}
                      </Text>
                    </Flex>
                    <Divider borderColor="gray.500" borderWidth="1px" />
  
                    
                  </VStack>

                  <Box
                    pos='relative'
                    bg="secondary"
                    color="white"
                    mt={4}
                    p={{ base: 4, md: 6 }}
                    borderRadius="md"
                  >
                    <Text
                      fontFamily="Lexend Deca"
                      fontSize={{ base: "lg", md: "xl" }}
                      textAlign="center"
                    >
                      Resultado da Simulação
                    </Text>

                    {valorSeguro && (
                      <Box pos='absolute' top='1.66rem' right={10}>
                        <Text
                          fontFamily="Lexend Deca"
                          fontSize={{ base: "lg" }}
                          textAlign="center"
                        >
                          {`Seguro: ${BrlCurrencyFormatter(valorSeguro)}`}
                        </Text>
                      </Box>
                    )}


                    <Flex width="100%" justifyContent="center" gap='8rem' mt={4}>
                      <VStack spacing={4}>
                        <Flex direction="column" align="start" width="100%">
                          <Text
                            fontSize={{ base: "xl", md: "2xl" }}
                            fontFamily="Lexend Deca"
                          >
                            {BrlCurrencyFormatter(
                              oferta?.releasedAmount
                            )}
                          </Text>
                          <Text
                            fontSize={{ base: "sm", md: "md" }}
                            fontFamily="Lexend Deca"
                          >
                            VALOR LIBERADO AO CLIENTE
                          </Text>
                        </Flex>

                        <Flex direction="column" align="start" width="100%">
                          <Text
                            fontSize={{ base: "xl", md: "2xl" }}
                            fontFamily="Lexend Deca"
                          >
                            {oferta?.numberOfInstallments ? `${oferta.numberOfInstallments} x ${BrlCurrencyFormatter(
                                  oferta.installmentValue
                                )}`
                              : oferta?.numberOfInstallments ?? "Valor não disponível"}
                          </Text>
                          <Text
                            fontSize={{ base: "sm", md: "md" }}
                            fontFamily="Lexend Deca"
                          >
                            PRAZO DO CONTRATO
                          </Text>
                        </Flex>
                      </VStack>

                      <VStack spacing={4}>
                        <Flex direction="column" align="start" width="100%">
                          <Text
                            fontSize={{ base: "xl", md: "2xl" }}
                            fontFamily="Lexend Deca"
                          >
                            {BrlCurrencyFormatter(oferta?.loanAmount)}
                          </Text>
                          <Text
                            fontSize={{ base: "sm", md: "md" }}
                            fontFamily="Lexend Deca"
                          >
                            VALOR DESCONTADO
                          </Text>
                        </Flex>

                        <Flex direction="column" align="start" width="100%">
                          <Text
                            fontSize={{ base: "xl", md: "2xl" }}
                            fontFamily="Lexend Deca"
                          >
                            {oferta?.monthlyRate ? oferta.monthlyRate.toFixed(2) : "XXXXX"} %
                          </Text>
                          <Text
                            fontSize={{ base: "sm", md: "md" }}
                            fontFamily="Lexend Deca"
                          >
                            TAXA A.M.
                          </Text>
                        </Flex>
                      </VStack>
                    </Flex>

                    <Flex justify="center" mt={6}>
                      {
                        !(proposal?.status_proposta === ProposalStatus.CANCELED || proposal?.status_proposta === ProposalStatus.PAYED) && (
                          <Button
                            bg="white"
                            color="secondary"
                            fontFamily="Lexend Deca"
                            borderRadius="md"
                            _hover={{ bg: "#f5f5f5" }}
                            onClick={(event) =>
                              handleCancelProposal(event, proposal?.id_proposta!)
                            }
                            px={{ base: 4, md: 8 }}
                            w={{ base: "100%", md: "auto" }}

                          >
                            Cancelar Proposta
                          </Button>
                        )
                      }
                    </Flex>
                  </Box>
                </>
              )}

              {activeTab === "seguranca" && (
                <Box p={{ base: 2, md: 4 }}>
                  <Flex
                    align="center"
                    mb={4}
                    flexDirection={{ base: "column", md: "row" }}
                  >
                    <Text
                      fontSize={{ base: "2xl", md: "xl" }}
                      fontFamily="Lexend Deca"
                      color="gray.500"
                      mr={{ base: 0, md: 2 }}
                      mb={{ base: 2, md: 0 }}
                    >
                      Status Formalização:
                    </Text>
                    <Badge
                      bg="#58c46c"
                      color="white"
                      borderRadius="4px"
                      fontSize={{ base: "sm", md: "md" }}
                      p={{ base: "2px 6px", md: "4px 8px" }}
                    >
                      {getFormalizationStatusLabel(
                        proposal?.status_unico!
                      )}
                    </Badge>
                  </Flex>
                  {(proposal?.foto_formalizacao ||
                    proposal?.documento_formalizacao ||
                    proposal?.localizacao) && (
                      <Card shadow="md">
                        <CardHeader pb={0}>
                          <Text
                            fontSize={{ base: "2xl", md: "xl" }}
                            fontFamily="Lexend Deca"
                            color="gray.500"
                          >
                            Biometria:
                          </Text>
                        </CardHeader>
                        <CardBody
                          pt={0}
                        // mt={0}
                        >
                          <Flex w="100%" justifyContent="space-between" flexWrap="wrap">
                            {proposal?.foto_formalizacao && (
                              <Box
                                minW={{ base: "100%", md: "45%" }}
                                maxW={{ base: "100%", md: "48%" }}
                              >
                                <Text
                                  fontSize={{ base: "small", md: "medium" }}
                                  fontFamily="Lexend Deca"
                                  color="gray.500"
                                  align="center"
                                  mt={4}
                                >
                                  Selfie
                                </Text>

                                <Box
                                  mt="0.5rem"
                                  width="100%"
                                  height="10rem"
                                  overflow="hidden"
                                  borderRadius="md"
                                  borderWidth="0.0625rem"
                                >
                                  <FilePreview fileUrl={proposal?.foto_formalizacao!} />
                                </Box>
                              </Box>
                            )}

                            {proposal?.documento_formalizacao && (
                              <Box
                                minW={{ base: "100%", md: "45%" }}
                                maxW={{ base: "100%", md: "48%" }}
                              >
                                <Text
                                  fontSize={{ base: "small", md: "medium" }}
                                  fontFamily="Lexend Deca"
                                  color="gray.500"
                                  align="center"
                                  mt={4}
                                >
                                  DOC
                                </Text>
                                <Box
                                  mt="0.5rem"
                                  width="100%"
                                  height="10rem"
                                  overflow="hidden"
                                  borderRadius="md"
                                  borderWidth="0.0625rem"
                                  display="flex"
                                  alignItems="center"
                                  justifyContent="center"
                                >
                                  <FilePreview
                                    fileUrl={proposal?.documento_formalizacao!}
                                  />
                                </Box>
                              </Box>
                            )}

                            {proposal?.documento_formalizacao_verso && (
                              <Box
                                minW={{ base: "100%", md: "45%" }}
                                maxW={{ base: "100%", md: "48%" }}
                              >
                                <Text
                                  fontSize={{ base: "small", md: "medium" }}
                                  fontFamily="Lexend Deca"
                                  color="gray.500"
                                  align="center"
                                  mt={4}
                                >
                                  DOC Verso
                                </Text>
                                <Box
                                  mt="0.5rem"
                                  width="100%"
                                  height="10rem"
                                  overflow="hidden"
                                  borderRadius="md"
                                  borderWidth="0.0625rem"
                                  display="flex"
                                  alignItems="center"
                                  justifyContent="center"
                                >
                                  <FilePreview
                                    fileUrl={proposal?.documento_formalizacao_verso!}
                                  />
                                </Box>
                              </Box>
                            )}
                          </Flex>

                          {proposal?.localizacao && (
                            <Box>
                              <Divider mb={4} mt={6} />

                              <Box>
                                <Text
                                  fontSize={{ base: "2xl", md: "xl" }}
                                  fontFamily="Lexend Deca"
                                  color="gray.500"
                                >
                                  Localização:
                                </Text>
                                <Button
                                  display={"flex"}
                                  alignItems={"center"}
                                  gap={2}
                                  // bg="white"
                                  colorScheme="primary"
                                  fontFamily="Lexend Deca"
                                  borderRadius="md"
                                  // _hover={{ bg: "#f5f5f5" }}
                                  onClick={() => handleViewMap(proposal?.localizacao!)}
                                  // px={{ base: 4, md: 8 }}
                                  mt={2}
                                  size="sm"
                                // w={{ base: "100%", md: "auto" }}
                                >
                                  <MdLocationOn />
                                  Abrir no Google Maps
                                </Button>
                              </Box>
                            </Box>
                          )}
                        </CardBody>
                      </Card>
                    )}
                </Box>
              )}

              {activeTab === "analise-credito" && (
                <Box p={{ base: 2, md: 4 }}>
                  <CreditEngineReportProposal proposalId={proposalId} />
                </Box>
              )}

              {activeTab === "disparo-mensagem" && (
                <Box p={{ base: 2, md: 4 }}>
                  <ReportProposalSendEmail proposalId={proposalId} />
                </Box>
              )}

              {activeTab === "contrato" && (
                <Box p={{ base: 2, md: 4 }}>
                  <Grid templateColumns='repeat(2, 1fr)'>
                    <GridItem>
                      <Box>
                        <Text
                          fontSize={{ base: "md", md: "lg" }}
                          fontFamily="Lexend Deca"
                          color="gray.500"
                          mb={2}
                        >
                          CONTRATO
                        </Text>
                        {(proposal?.contrato_assinado_url || proposal?.contrato_url) && (
                          <Link
                            href={`https://docs.google.com/viewer?url=${encodeURIComponent(proposal?.contrato_assinado_url || proposal?.contrato_url || '')}&embedded=true`}
                            target="_blank"
                            rel="noopener noreferrer"
                            bg="secondary"
                            color="white"
                            borderRadius="full"
                            fontSize={{ base: "md", md: "lg" }}
                            fontFamily="Lexend Deca"
                            px={{ base: 6, md: 8 }}
                            py={2}
                            mb={{ base: 2, md: 0 }}
                            _hover={{ bg: "#064d60" }}
                            display="inline-flex"
                            alignItems="center"
                            justifyContent="center"
                          >
                            <Flex align="center" justify="center">
                              <Icon
                                as={FaFileDownload}
                                boxSize={{ base: 5, md: 6 }}
                                mr={3}
                              />
                              Contrato
                            </Flex>
                          </Link>
                        )}
                      </Box>

                      {(proposal?.seguro_contrato_assinado_url || proposal?.seguro_contrato_url) && (
                        <Box mt={6}>
                          <Text
                            fontSize={{ base: "md", md: "lg" }}
                            fontFamily="Lexend Deca"
                            color="gray.500"
                            mb={2}
                          >
                            APÓLICE
                          </Text>
                          <Link
                            href={`https://docs.google.com/viewer?url=${encodeURIComponent(proposal?.seguro_contrato_assinado_url || proposal?.seguro_contrato_url || '')}&embedded=true`}
                            target="_blank"
                            rel="noopener noreferrer"
                            bg="secondary"
                            color="white"
                            borderRadius="full"
                            fontSize={{ base: "md", md: "lg" }}
                            fontFamily="Lexend Deca"
                            px={{ base: 6, md: 8 }}
                            py={2}
                            mb={{ base: 2, md: 0 }}
                            _hover={{ bg: "#064d60" }}
                            display="inline-flex"
                            alignItems="center"
                            justifyContent="center"
                          >
                            <Flex align="center" justify="center">
                              <Icon
                                as={FaFileDownload}
                                boxSize={{ base: 5, md: 6 }}
                                mr={3}
                              />
                              Apólice
                            </Flex>
                          </Link>
                        </Box>
                      )}

                    </GridItem>

                    <GridItem>
                      <Box>
                        {proposal?.recibo_pagamento_url && (
                          <Box mb={6}>
                            <Text
                              fontSize={{ base: "md", md: "lg" }}
                              fontFamily="Lexend Deca"
                              color="gray.500"
                              textTransform='uppercase'
                              mb={2}
                            >
                              PAGAMENTO
                            </Text>

                            <Link
                              href={`https://docs.google.com/viewer?url=${encodeURIComponent(proposal.recibo_pagamento_url)}&embedded=true`}
                              target="_blank"
                              rel="noopener noreferrer"
                              bg="#58c46c"
                              color="white"
                              borderRadius="full"
                              fontSize={{ base: "md", md: "lg" }}
                              fontFamily="Lexend Deca"
                              px={{ base: 6, md: 8 }}
                              py={2}
                              _hover={{ bg: "green.500" }}
                              display="inline-flex"
                              alignItems="center"
                              justifyContent="center"
                              width='100%'
                            >
                              <Flex align="center" justify="center">
                                <Icon
                                  as={FaFileAlt}
                                  boxSize={{ base: 5, md: 6 }}
                                  mr={3}
                                />
                                Comprovante de Pagamento
                              </Flex>
                            </Link>
                          </Box>
                        )}

                        {proposal?.comprovante_averbacao_url && (
                          <Box mb={6}>
                            <Text
                              fontSize={{ base: "md", md: "lg" }}
                              fontFamily="Lexend Deca"
                              color="gray.500"
                              textTransform='uppercase'
                              mb={2}
                            >
                              Comprovante de Averbação
                            </Text>

                            <Link
                              href={`https://docs.google.com/viewer?url=${encodeURIComponent(proposal.comprovante_averbacao_url)}&embedded=true`}
                              target="_blank"
                              rel="noopener noreferrer"
                              bg="#58c46c"
                              color="white"
                              borderRadius="full"
                              fontSize={{ base: "md", md: "lg" }}
                              fontFamily="Lexend Deca"
                              px={{ base: 6, md: 8 }}
                              py={2}
                              _hover={{ bg: "green.500" }}
                              display="inline-flex"
                              alignItems="center"
                              justifyContent="center"
                              width='100%'
                            >
                              Comprovante de Averbação
                            </Link>
                          </Box>
                        )}

                        {proposal?.comprovante_cancelamento_url && (
                          <Box>
                            <Text
                              fontSize={{ base: "md", md: "lg" }}
                              fontFamily="Lexend Deca"
                              color="gray.500"
                              mb={2}
                              textTransform='uppercase'
                            >
                              Comprovante de Cancelamento
                            </Text>

                            <Link
                              href={`https://docs.google.com/viewer?url=${encodeURIComponent(proposal.comprovante_cancelamento_url)}&embedded=true`}
                              target="_blank"
                              rel="noopener noreferrer"
                              bg="secondary"
                              color="white"
                              borderRadius="full"
                              fontSize={{ base: "md", md: "lg" }}
                              fontFamily="Lexend Deca"
                              px={{ base: 6, md: 8 }}
                              py={2}
                              width='100%'
                              _hover={{ bg: "#064d60" }}
                              display="inline-flex"
                              alignItems="center"
                              justifyContent="center"
                            >
                              Comprovante de Cancelamento
                            </Link>
                          </Box>
                        )}
                      </Box>
                    </GridItem>
                  </Grid>
                </Box>
              )}

              {activeTab === "historico" && (() => (
                <ProposalLogs logs={JSON.parse(proposal.custom_status as any) as ProposalConsignadoCustomStatus} proposalId={proposal?.id_proposta || ""} />
              )
              )()}
              {activeTab === "ações" && (
                <Flex justify="center" gap={4}>

                </Flex>
              )}
            </ModalBody>
          </>)}
        </ModalContent>
      </Modal>
      <BankDetailModal
        isOpen={isBankDetailFormOpen}
        onClose={() => setIsBankDetailFormOpen(false)}
        proposalId={proposal?.id_proposta}
      />
      <BankAndPersonalDetailModal
        isOpen={isBankAndPersonalDetailFormOpen}
        onClose={() => setIsBankAndPersonalDetailFormOpen(false)}
        proposalId={proposal?.id_proposta}
      />
    </>
  );
}
